import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WbButton from '../common/WbButton';
import WbTextField from '../common/WbTextField';
import { getApiHookParams } from '../../auth';
import participantsApi from '../../services/api/participants/participantsApi';
import kioskApi from '../../services/api/kiosk/kioskApi';
import paths from '../../routes/paths';
import '../../views/Kiosk/KioskStyles.scss';

export default function EnterKioskMode() {
  const navigate = useNavigate();
  const apiParams = getApiHookParams();
  const [open, setOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [loading, setLoading] = useState(false);
  const isMat = apiParams.groupId > 0 && apiParams.schoolId === null;

  useEffect(() => {
    if (open && isMat) {
      loadMatSchools();
    }
  }, [open, isMat]);

  const loadMatSchools = async () => {
    try {
      setLoading(true);
      const matSchools = await participantsApi.getSchoolsByMatId(apiParams.groupId);
      // Filter to only accessible schools
      const accessibleSchools = apiParams.schoolIds && apiParams.schoolIds.length > 0
        ? matSchools.filter(s => apiParams.schoolIds.includes(s.id))
        : matSchools;
      setSchools(accessibleSchools || []);
      if (accessibleSchools.length === 1) {
        setSelectedSchoolId(accessibleSchools[0].id);
      }
    } catch (err) {
      console.error('Error loading MAT schools:', err);
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterKiosk = async () => {
    if (isMat) {
      // MAT: show dialog to select school
      setOpen(true);
    } else {
      // School-level: get slug from KioskAdmin status and go directly to kiosk
      if (!apiParams.schoolId) return;
      try {
        setLoading(true);
        const status = await kioskApi.getKioskStatus(apiParams.schoolId);
        const slug = status?.schoolSlug || status?.slug;
        if (!slug) {
          console.error('Kiosk status did not return a slug', status);
          return;
        }
        navigate(paths.kioskSchool.replace(':schoolSlug', slug));
      } catch (err) {
        console.error('Error getting kiosk status for school:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSchoolSelect = async () => {
    if (!selectedSchoolId) return;
    
    try {
      setLoading(true);
      const status = await kioskApi.getKioskStatus(selectedSchoolId);
      const slug = status?.schoolSlug || status?.slug;
      if (!slug) {
        console.error('Kiosk status did not return a slug for selected school', status);
        return;
      }
      setOpen(false);
      navigate(paths.kioskSchool.replace(':schoolSlug', slug));
    } catch (err) {
      console.error('Error getting kiosk status for selected school:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        // sx={{
        //   position: 'fixed',
        //   bottom: '16px',
        //   left: '16px',
        //   zIndex: 1000,
        // }}
      >
        <WbButton
          CustomButtonText="Enter Kiosk Mode"
          onClick={handleEnterKiosk}
          // Do NOT use kiosk button classes here to avoid huge min-width
          sx={{
            // Compact, unobtrusive button on Dashboard, match primary sky blue
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 0',
            width: '100%',            
            backgroundColor: '#53B5E0',
            borderColor: '#53B5E0',
            color: '#fff',            
          }}
        />
      </Box>

      {/* School Selection Dialog for MAT */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'center' }}>
          Select School for Kiosk Mode
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <WbTextField
              select
              label="Select School"
              value={selectedSchoolId}
              onChange={(e) => setSelectedSchoolId(e.target.value)}
              fullWidth
              disabled={loading}
            >
              {schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name || school.schoolName}
                </MenuItem>
              ))}
            </WbTextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: '12px 20px', gap: 1.5 }}>
          <WbButton
            CustomButtonText="Cancel"
            onClick={() => setOpen(false)}
            sx={{
              fontSize: '14px',
              padding: '6px 18px',
              minWidth: '120px',
              backgroundColor: '#6B7280',
              color: 'white',
              '&:hover': { backgroundColor: '#4B5563' },
            }}
          />
          <WbButton
            CustomButtonText="Enter Kiosk Mode"
            onClick={handleSchoolSelect}
            disabled={!selectedSchoolId || loading}
            sx={{
              fontSize: '14px',
              padding: '6px 22px',
              minWidth: '150px',
              backgroundColor: 'var(--kiosk-primary-color, #53B5E0)',
              color: 'white',
              '&:hover': { backgroundColor: '#3a9bc4' },
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
